"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Info, ChevronDown, ChevronUp, Plus, ShoppingCart, AlertCircle, X } from "lucide-react";
import { estimate, type PharmaCarePlan } from "@/lib/coverage/bc-pharmacare";
import { analytics } from "@/lib/analytics/core";
import {
  CHECKOUT_INSURANCE_PROVIDERS,
  InsuranceProviderDropdown,
  resolveInsurerName,
} from "./insurance-providers";
import { useCurrency } from "@/context/CurrencyContext";

const COVERAGE_PLANS: PharmaCarePlan[] = [
  "fair-pharmacare",
  "plan-c",
  "plan-b",
  "plan-f",
  "plan-g",
  "plan-p",
  "plan-w",
  "none",
];

type CoverageInsuranceEntryDetail = {
  provider?: string;
  providerOther?: string;
  pct?: string | number;
  annualMax?: string | number;
  ytdUsed?: string | number;
};

type CoverageUpdateDetail = {
  plan?: PharmaCarePlan | string;
  drugPrice?: string | number;
  income?: string | number;
  ytdSpend?: string | number;
  isLca?: boolean;
  hasInsurance?: boolean;
  /** New: ordered list of plans (primary first, up to 3). Replaces the legacy single-field shape. */
  insuranceEntries?: CoverageInsuranceEntryDetail[];
  /** Legacy single-insurance fields — still accepted for backwards-compatible voice tool calls. Applied to entry[0]. */
  insuranceProvider?: string;
  insuranceProviderOther?: string;
  insurancePct?: string | number;
  insuranceAnnualMax?: string | number;
  insuranceYtdUsed?: string | number;
};

type InsuranceEntry = {
  provider: string;
  providerOther: string;
  pct: string;
  annualMax: string;
  ytdUsed: string;
};

const MAX_INSURANCE_ENTRIES = 3;
const ENTRY_LABELS = ["Primary insurance", "Secondary insurance", "Tertiary insurance"];

const EMPTY_INSURANCE_ENTRY: InsuranceEntry = {
  provider: "",
  providerOther: "",
  pct: "80",
  annualMax: "",
  ytdUsed: "",
};

type PlanOption = {
  value: PharmaCarePlan;
  label: string;
  tag: string;
  hint: string;
};

const PLAN_OPTIONS: PlanOption[] = [
  {
    value: "fair-pharmacare",
    label: "Fair PharmaCare",
    tag: "Plan I",
    hint: "Most BC residents — coverage scales with your family income.",
  },
  {
    value: "plan-c",
    label: "Income Assistance",
    tag: "Plan C",
    hint: "100% of eligible drugs covered — no deductible.",
  },
  {
    value: "plan-b",
    label: "Long-Term Care",
    tag: "Plan B",
    hint: "Licensed long-term care facility residents.",
  },
  {
    value: "plan-f",
    label: "At-Home Program",
    tag: "Plan F",
    hint: "Children with severe disabilities.",
  },
  {
    value: "plan-g",
    label: "Mental Health",
    tag: "Plan G",
    hint: "Eligible psychiatric medications.",
  },
  {
    value: "plan-p",
    label: "Palliative",
    tag: "Plan P",
    hint: "Patients enrolled in palliative care.",
  },
  {
    value: "plan-w",
    label: "First Nations",
    tag: "Plan W",
    hint: "Status First Nations clients in BC.",
  },
  {
    value: "none",
    label: "No public plan",
    tag: "—",
    hint: "Estimate the retail price only.",
  },
];

const COLORS = {
  you: "#e05574",
  insurance: "#a78bfa",
  pharmacare: "#0f3460",
};

/* ── Animated number hook ── */

function useAnimatedNumber(target: number, duration = 450): number {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === display) return;
    const from = fromRef.current;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = from + (target - from) * eased;
      setDisplay(next);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
        setDisplay(target);
      }
    };

    fromRef.current = display;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return display;
}

function AnimatedDollar({ value, className }: { value: number; className?: string }) {
  const v = useAnimatedNumber(value);
  const { formatPrice } = useCurrency();
  return <span className={`tabular-nums ${className ?? ""}`}>{formatPrice(v)}</span>;
}

/* ── Plan dropdown ── */

function PlanDropdown({
  plan,
  onChange,
}: {
  plan: PharmaCarePlan;
  onChange: (p: PharmaCarePlan) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selected = PLAN_OPTIONS.find((p) => p.value === plan) ?? PLAN_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e05574]/40 ${
          open
            ? "border-[#e05574] bg-[#fdf3f5] dark:border-[#e05574] dark:bg-[#2a1418]"
            : "border-[#ebebea] bg-white hover:border-[#d0d0ce] dark:border-white/10 dark:bg-[#0f1419] dark:hover:border-white/20"
        }`}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="shrink-0 rounded-md bg-[#fdf3f5] px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#e05574] dark:bg-[#2a1418]">
            {selected.tag}
          </span>
          <span className="truncate text-sm font-semibold text-[#111] dark:text-white">
            {selected.label}
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-[#888] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-72 overflow-auto rounded-xl border border-[#e8e8e5] bg-white p-1 shadow-xl dark:border-white/10 dark:bg-[#1a1f25]"
        >
          {PLAN_OPTIONS.map((opt) => {
            const isActive = plan === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${
                  isActive
                    ? "bg-[#fdf3f5] dark:bg-[#2a1418]"
                    : "hover:bg-[#fafaf9] dark:hover:bg-white/5"
                }`}
              >
                <span
                  className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    isActive
                      ? "bg-[#e05574] text-white"
                      : "bg-[#f0f0ee] text-[#888] dark:bg-white/10 dark:text-white/40"
                  }`}
                >
                  {opt.tag}
                </span>
                <span
                  className={`flex-1 truncate text-sm font-semibold ${
                    isActive
                      ? "text-[#b83058] dark:text-[#e8798f]"
                      : "text-[#111] dark:text-white"
                  }`}
                >
                  {opt.label}
                </span>
                {isActive && (
                  <span className="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#e05574]">
                    <svg
                      className="h-3 w-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="3.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m5 13 4 4L19 7"
                      />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Currency input ── */

function CurrencyInput({
  label,
  sublabel,
  value,
  onChange,
  placeholder = "0.00",
}: {
  label: React.ReactNode;
  sublabel?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-[#111] dark:text-white">{label}</span>
      <div className="relative mt-1.5">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-sm font-medium text-[#888] dark:text-white/50">
          $
        </span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-[#e8e8e5] bg-white py-2.5 pl-10 pr-3 text-sm text-[#111] focus:border-[#e05574] focus:outline-none focus:ring-2 focus:ring-[#e05574]/20 dark:border-white/10 dark:bg-[#0f1419] dark:text-white"
        />
      </div>
      {sublabel && (
        <p className="mt-1 text-xs text-[#888] dark:text-white/50">{sublabel}</p>
      )}
    </label>
  );
}

/* ── Estimator ── */

export default function CoverageEstimator({ prefillPrice }: { prefillPrice?: number } = {}) {
  const { formatPrice } = useCurrency();
  const [plan, setPlan] = useState<PharmaCarePlan>("fair-pharmacare");
  const [drugPrice, setDrugPrice] = useState<string>(
    prefillPrice ? prefillPrice.toFixed(2) : "",
  );
  const [income, setIncome] = useState("");
  const [ytdSpend, setYtdSpend] = useState("");
  const [isLca, setIsLca] = useState(true);

  const [hasInsurance, setHasInsurance] = useState(false);
  const [insuranceEntries, setInsuranceEntries] = useState<InsuranceEntry[]>(
    () => [{ ...EMPTY_INSURANCE_ENTRY }],
  );

  const updateEntry = (idx: number, patch: Partial<InsuranceEntry>) => {
    setInsuranceEntries((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
    );
  };

  const removeEntry = (idx: number) => {
    setInsuranceEntries((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length > 0 ? next : [{ ...EMPTY_INSURANCE_ENTRY }];
    });
  };

  const addEntry = () => {
    setInsuranceEntries((prev) =>
      prev.length >= MAX_INSURANCE_ENTRIES ? prev : [...prev, { ...EMPTY_INSURANCE_ENTRY }],
    );
  };

  useEffect(() => {
    if (prefillPrice && drugPrice === "") {
      setDrugPrice(prefillPrice.toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPrice]);

  const incomeRequired = plan === "fair-pharmacare";
  const drugPriceNum = Number(drugPrice) || 0;
  const incomeNum = Number(income) || 0;
  const ready = drugPriceNum > 0 && (!incomeRequired || incomeNum > 0);

  const result = useMemo(() => {
    if (!ready) return null;
    return estimate({
      plan,
      drugPrice: drugPriceNum,
      isLcaGeneric: isLca,
      netFamilyIncome: incomeRequired ? incomeNum : undefined,
      ytdEligibleSpend: ytdSpend ? Number(ytdSpend) : undefined,
    });
  }, [plan, drugPriceNum, incomeRequired, incomeNum, ytdSpend, isLca, ready]);

  const insurance = useMemo(() => {
    const patientPays = result?.patientPaysCad ?? 0;
    if (!hasInsurance || insuranceEntries.length === 0) {
      return {
        insurancePays: 0,
        finalPatient: patientPays,
        breakdown: [] as { provider: string; providerName: string | null; pays: number }[],
      };
    }
    // Apply each insurer sequentially against what the patient still owes.
    // Primary pays first, then secondary on the remaining amount, etc.
    let remaining = patientPays;
    let totalInsurancePays = 0;
    const breakdown: { provider: string; providerName: string | null; pays: number }[] = [];
    for (const entry of insuranceEntries) {
      const providerName = resolveInsurerName(entry.provider, entry.providerOther);
      const rawPct = Number(entry.pct);
      const pct = Number.isFinite(rawPct) ? Math.min(100, Math.max(0, rawPct)) / 100 : 0;
      const annualMax = entry.annualMax ? Number(entry.annualMax) : Infinity;
      const ytdUsed = entry.ytdUsed ? Number(entry.ytdUsed) : 0;
      const room = Math.max(0, annualMax - ytdUsed);
      const pays = remaining > 0 && pct > 0 ? Math.min(remaining * pct, room) : 0;
      totalInsurancePays += pays;
      remaining = Math.max(0, remaining - pays);
      breakdown.push({ provider: entry.provider, providerName, pays });
    }
    return {
      insurancePays: totalInsurancePays,
      finalPatient: Math.max(0, patientPays - totalInsurancePays),
      breakdown,
    };
  }, [result, hasInsurance, insuranceEntries]);

  // ── Agent integration ─────────────────────────────────────────────
  // Mirror state onto window.__bpCoverage so the agent can read live
  // values via getCoverageState RPC without scraping the DOM, and
  // listen for agent-update-coverage events to set fields by voice.
  useEffect(() => {
    const w = window as unknown as { __bpCoverage?: unknown };
    const primary = insuranceEntries[0];
    const primaryName = primary
      ? resolveInsurerName(primary.provider, primary.providerOther)
      : null;
    w.__bpCoverage = {
      plan,
      drugPrice,
      income,
      ytdSpend,
      isLca,
      hasInsurance,
      // Ordered list of all configured plans (primary first). Each
      // entry carries its raw input + the resolved insurer name + how
      // much that insurer pays after sequential application.
      insuranceEntries: insuranceEntries.map((e, i) => ({
        position: i,
        provider: e.provider,
        providerOther: e.providerOther,
        providerName: resolveInsurerName(e.provider, e.providerOther),
        pct: e.pct,
        annualMax: e.annualMax,
        ytdUsed: e.ytdUsed,
        pays: insurance.breakdown[i]?.pays ?? 0,
      })),
      // Legacy single-insurance keys (kept for backwards-compatible
      // agent tools/RPC consumers — mirror the primary plan).
      insuranceProvider: primary?.provider ?? "",
      insuranceProviderOther: primary?.providerOther ?? "",
      insuranceProviderName: primaryName,
      insurancePct: primary?.pct ?? "",
      insuranceAnnualMax: primary?.annualMax ?? "",
      insuranceYtdUsed: primary?.ytdUsed ?? "",
      ready,
      result: result
        ? {
            patientPaysCad: result.patientPaysCad,
            pharmaCarePaysCad: result.pharmaCarePaysCad,
            annualDeductibleCad: result.annualDeductibleCad,
          }
        : null,
      insurancePays: insurance.insurancePays,
      finalPatient: insurance.finalPatient,
    };
    return () => {
      delete w.__bpCoverage;
    };
  }, [
    plan,
    drugPrice,
    income,
    ytdSpend,
    isLca,
    hasInsurance,
    insuranceEntries,
    ready,
    result,
    insurance.insurancePays,
    insurance.finalPatient,
    insurance.breakdown,
  ]);

  useEffect(() => {
    const toStr = (v: string | number | undefined): string | undefined =>
      v === undefined ? undefined : typeof v === "number" ? String(v) : v;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CoverageUpdateDetail>).detail;
      if (!detail || typeof detail !== "object") return;

      if (typeof detail.plan === "string" && (COVERAGE_PLANS as string[]).includes(detail.plan)) {
        setPlan(detail.plan as PharmaCarePlan);
      }
      const setStr = (
        v: string | number | undefined,
        setter: (s: string) => void,
      ) => {
        const s = toStr(v);
        if (s !== undefined) setter(s);
      };
      setStr(detail.drugPrice, setDrugPrice);
      setStr(detail.income, setIncome);
      setStr(detail.ytdSpend, setYtdSpend);
      if (typeof detail.isLca === "boolean") setIsLca(detail.isLca);
      if (typeof detail.hasInsurance === "boolean") setHasInsurance(detail.hasInsurance);

      // New shape: full ordered list. Replaces existing entries.
      if (Array.isArray(detail.insuranceEntries) && detail.insuranceEntries.length > 0) {
        const incoming = detail.insuranceEntries
          .slice(0, MAX_INSURANCE_ENTRIES)
          .map((src) => ({
            ...EMPTY_INSURANCE_ENTRY,
            ...(typeof src.provider === "string" ? { provider: src.provider } : {}),
            ...(typeof src.providerOther === "string" ? { providerOther: src.providerOther } : {}),
            ...(toStr(src.pct) !== undefined ? { pct: toStr(src.pct) as string } : {}),
            ...(toStr(src.annualMax) !== undefined ? { annualMax: toStr(src.annualMax) as string } : {}),
            ...(toStr(src.ytdUsed) !== undefined ? { ytdUsed: toStr(src.ytdUsed) as string } : {}),
          }));
        setInsuranceEntries(incoming);
      } else {
        // Legacy single-insurance shape — patch entry[0] in place so
        // callers can still tweak just the % or just the provider.
        const legacyKeys = [
          "insuranceProvider",
          "insuranceProviderOther",
          "insurancePct",
          "insuranceAnnualMax",
          "insuranceYtdUsed",
        ] as const;
        const hasLegacy = legacyKeys.some((k) => detail[k] !== undefined);
        if (hasLegacy) {
          setInsuranceEntries((prev) => {
            const head = prev[0] ?? { ...EMPTY_INSURANCE_ENTRY };
            const next: InsuranceEntry = { ...head };
            if (typeof detail.insuranceProvider === "string") {
              next.provider = detail.insuranceProvider;
              if (detail.insuranceProvider !== "other") next.providerOther = "";
            }
            if (typeof detail.insuranceProviderOther === "string") {
              next.providerOther = detail.insuranceProviderOther;
            }
            const pct = toStr(detail.insurancePct);
            if (pct !== undefined) next.pct = pct;
            const annualMax = toStr(detail.insuranceAnnualMax);
            if (annualMax !== undefined) next.annualMax = annualMax;
            const ytdUsed = toStr(detail.insuranceYtdUsed);
            if (ytdUsed !== undefined) next.ytdUsed = ytdUsed;
            return [next, ...prev.slice(1)];
          });
        }
      }
    };
    window.addEventListener("agent-update-coverage", handler);
    return () => window.removeEventListener("agent-update-coverage", handler);
  }, []);

  // ── GA4 telemetry ───────────────────────────────────────────────────
  // Fire once per mount when the user actually completes enough fields
  // to produce a result ("ran the numbers") and once when they first turn
  // private insurance on. Both signals matter for the funnel; firing per
  // keystroke would flood GA4 because the estimator recomputes live.
  const numbersRunTrackedRef = useRef(false);
  const insuranceAddedTrackedRef = useRef(false);
  const formSource = prefillPrice ? "product_page" : "coverage_page";

  useEffect(() => {
    if (!ready || !result || numbersRunTrackedRef.current) return;
    numbersRunTrackedRef.current = true;
    analytics.track("generate_lead", {
      form_name: "coverage_estimator",
      form_source: formSource,
      value: drugPriceNum,
      currency: "CAD",
    });
  }, [ready, result, drugPriceNum, formSource]);

  useEffect(() => {
    if (!hasInsurance || insuranceAddedTrackedRef.current) return;
    insuranceAddedTrackedRef.current = true;
    analytics.track("cta_click", {
      cta_id: "coverage-add-insurance",
      cta_text: "Add private insurance",
    });
  }, [hasInsurance]);

  return (
    <div className="overflow-hidden rounded-3xl border border-[#e8e8e5] bg-white shadow-sm dark:border-white/10 dark:bg-[#1a1f25]">
      <div className="grid grid-cols-1 sm:grid-cols-2">
        {/* ─────────────────  LEFT COLUMN — FORM  ───────────────── */}
        <div className="border-[#e8e8e5] dark:border-white/10 sm:border-r">
          {/* Plan picker */}
          <div className="border-b border-[#e8e8e5] p-6 dark:border-white/10">
            <p className="mb-3 text-base font-semibold text-[#111] dark:text-white">
              Which plan are you on?
            </p>
            <PlanDropdown plan={plan} onChange={setPlan} />
            <p className="mt-3 flex items-start gap-1.5 text-xs text-[#777] dark:text-white/50">
              <Info size={12} className="mt-0.5 shrink-0 text-[#aaa]" />
              {PLAN_OPTIONS.find((p) => p.value === plan)?.hint}
            </p>
          </div>

          {/* Prescription details */}
          <div className="border-b border-[#e8e8e5] p-6 dark:border-white/10">
            <p className="mb-4 text-base font-semibold text-[#111] dark:text-white">
              Prescription details
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CurrencyInput
                label={
                  <>
                    Drug price
                    {prefillPrice ? (
                      <span className="ml-1.5 rounded-md bg-[#fdf3f5] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#e05574] dark:bg-[#2a1418]">
                        Auto-filled
                      </span>
                    ) : null}
                  </>
                }
                value={drugPrice}
                onChange={setDrugPrice}
              />

              {incomeRequired && (
                <CurrencyInput
                  label="Family income"
                  sublabel="Line 23600 of your last NOA"
                  value={income}
                  onChange={setIncome}
                  placeholder="Annual net family income"
                />
              )}

              <CurrencyInput
                label={
                  <>
                    Drug spend this year{" "}
                    <span className="font-normal text-[#888]">(optional)</span>
                  </>
                }
                sublabel="Total you've paid for eligible prescriptions so far this year"
                value={ytdSpend}
                onChange={setYtdSpend}
              />
            </div>

            <label className="mt-4 flex cursor-pointer items-center gap-2.5 rounded-lg border border-[#ebebea] bg-[#fafaf9] px-3.5 py-2.5 transition hover:border-[#d8d8d6] hover:bg-[#f4f4f2] dark:border-white/10 dark:bg-[#0f1419] dark:hover:border-white/20 dark:hover:bg-[#15191f]">
              <input
                type="checkbox"
                checked={isLca}
                onChange={(e) => setIsLca(e.target.checked)}
                className="h-4 w-4 rounded border-[#ccc] accent-[#e05574]"
              />
              <span className="text-sm font-medium text-[#444] dark:text-white/80">
                Generic / low-cost alternative drug
              </span>
            </label>
          </div>

          {/* Private insurance — layers on after the BC PharmaCare plan
              chosen up top. WorkSafeBC sits inside the provider list. */}
          <div className="p-6">
            <button
              type="button"
              onClick={() => setHasInsurance((v) => !v)}
              className="flex w-full items-center justify-between text-left"
            >
              <span className="text-base font-semibold text-[#111] dark:text-white">
                Private insurance
                {hasInsurance && (
                  <span className="ml-2 rounded-full bg-[#e05574] px-2 py-0.5 text-xs font-semibold text-white">
                    Added
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1 text-xs text-[#888] dark:text-white/50">
                {hasInsurance ? (
                  <>
                    Remove <ChevronUp size={14} />
                  </>
                ) : (
                  <>
                    Add <ChevronDown size={14} />
                  </>
                )}
              </span>
            </button>

            {hasInsurance && (
              <div className="mt-4 space-y-5">
                {insuranceEntries.map((entry, idx) => {
                  const canRemove = insuranceEntries.length > 1;
                  return (
                    <div
                      key={idx}
                      className="rounded-2xl border border-[#ebebea] bg-[#fafaf9] p-4 dark:border-white/10 dark:bg-[#0f1419]/60"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-[#111] dark:text-white">
                          {ENTRY_LABELS[idx] ?? `Insurance ${idx + 1}`}
                        </span>
                        {canRemove && (
                          <button
                            type="button"
                            onClick={() => removeEntry(idx)}
                            className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-[#888] transition hover:bg-white hover:text-[#b83058] dark:text-white/50 dark:hover:bg-white/5 dark:hover:text-[#e8798f]"
                            aria-label={`Remove ${ENTRY_LABELS[idx] ?? `insurance ${idx + 1}`}`}
                          >
                            <X size={12} aria-hidden />
                            Remove
                          </button>
                        )}
                      </div>

                      <div>
                        <span className="mb-1.5 block text-sm font-semibold text-[#111] dark:text-white">
                          Provider{" "}
                          <span className="font-normal text-[#888]">(optional)</span>
                        </span>
                        <InsuranceProviderDropdown
                          value={entry.provider}
                          onChange={(v) => {
                            updateEntry(idx, {
                              provider: v,
                              providerOther: v === "other" ? entry.providerOther : "",
                            });
                          }}
                          providers={CHECKOUT_INSURANCE_PROVIDERS}
                          placeholder="Select your insurer or WorkSafeBC"
                        />
                        {entry.provider === "other" && (
                          <input
                            type="text"
                            value={entry.providerOther}
                            onChange={(e) => updateEntry(idx, { providerOther: e.target.value })}
                            placeholder="Type your provider's name"
                            className="mt-2 w-full rounded-xl border border-[#e8e8e5] bg-white px-3 py-2.5 text-sm text-[#111] focus:border-[#e05574] focus:outline-none focus:ring-2 focus:ring-[#e05574]/20 dark:border-white/10 dark:bg-[#0f1419] dark:text-white"
                          />
                        )}
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <label className="block">
                          <span className="text-sm font-semibold text-[#111] dark:text-white">
                            Coverage rate
                          </span>
                          <div className="relative mt-1.5">
                            <input
                              type="number"
                              inputMode="numeric"
                              min="0"
                              max="100"
                              step="1"
                              value={entry.pct}
                              onChange={(e) => updateEntry(idx, { pct: e.target.value })}
                              placeholder="80"
                              className="w-full rounded-xl border border-[#e8e8e5] bg-white py-2.5 pl-3 pr-8 text-sm text-[#111] focus:border-[#e05574] focus:outline-none focus:ring-2 focus:ring-[#e05574]/20 dark:border-white/10 dark:bg-[#0f1419] dark:text-white"
                            />
                            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-[#888] dark:text-white/50">
                              %
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-[#888] dark:text-white/50">
                            {idx === 0
                              ? "% this insurer pays of your out-of-pocket"
                              : "% this insurer pays of what's left after the prior plan(s)"}
                          </p>
                        </label>

                        <CurrencyInput
                          label={
                            <>
                              Annual max{" "}
                              <span className="font-normal text-[#888]">(optional)</span>
                            </>
                          }
                          sublabel="This plan's yearly drug benefit limit"
                          value={entry.annualMax}
                          onChange={(v) => updateEntry(idx, { annualMax: v })}
                          placeholder="e.g. 1500"
                        />

                        <CurrencyInput
                          label={
                            <>
                              Used this year{" "}
                              <span className="font-normal text-[#888]">(optional)</span>
                            </>
                          }
                          sublabel="How much of this plan's annual max you've claimed so far"
                          value={entry.ytdUsed}
                          onChange={(v) => updateEntry(idx, { ytdUsed: v })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  );
                })}

                {insuranceEntries.length < MAX_INSURANCE_ENTRIES && (
                  <button
                    type="button"
                    onClick={addEntry}
                    className="flex items-center gap-1.5 rounded-xl border border-dashed border-[#ebebea] px-3 py-2 text-xs font-semibold text-[#595959] transition hover:border-[#e05574] hover:text-[#e05574] dark:border-white/10 dark:text-gray-300 dark:hover:border-[#e05574] dark:hover:text-[#e8798f]"
                  >
                    <Plus size={14} aria-hidden />
                    Add another plan
                  </button>
                )}

                <p className="text-xs text-[#888] dark:text-white/50">
                  BC PharmaCare adjudicates first; what&apos;s left flows to the
                  plans below in order — primary, then secondary, then tertiary.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ─────────────────  RIGHT COLUMN — LIVE RESULT  ───────────────── */}
        <ResultPanel
          ready={ready}
          incomeRequired={incomeRequired}
          result={result}
          insurance={insurance}
          hasInsurance={hasInsurance}
          insuranceEntries={insuranceEntries}
          prefillPrice={prefillPrice}
        />
      </div>
    </div>
  );
}

/* ── Result panel ── */

function ResultPanel({
  ready,
  incomeRequired,
  result,
  insurance,
  hasInsurance,
  insuranceEntries,
  prefillPrice,
}: {
  ready: boolean;
  incomeRequired: boolean;
  result: ReturnType<typeof estimate> | null;
  insurance: {
    insurancePays: number;
    finalPatient: number;
    breakdown: { provider: string; providerName: string | null; pays: number }[];
  };
  hasInsurance: boolean;
  insuranceEntries: InsuranceEntry[];
  prefillPrice?: number;
}) {
  const { formatPrice } = useCurrency();
  const total = result ? result.patientPaysCad + result.pharmaCarePaysCad : 0;

  const segments = result && total > 0
    ? [
        { label: "You pay", value: insurance.finalPatient, color: COLORS.you },
        ...(hasInsurance && insurance.insurancePays > 0
          ? [{ label: "Insurance", value: insurance.insurancePays, color: COLORS.insurance }]
          : []),
        { label: "PharmaCare", value: result.pharmaCarePaysCad, color: COLORS.pharmacare },
      ].filter((s) => s.value > 0)
    : [];

  // Per-plan footer lines: "Insurance room remaining for Sun Life: $X of $Y".
  const insuranceRoomLines = hasInsurance
    ? insuranceEntries
        .map((entry, i) => {
          const annualMax = entry.annualMax ? Number(entry.annualMax) : 0;
          if (!annualMax) return null;
          const ytdUsed = entry.ytdUsed ? Number(entry.ytdUsed) : 0;
          const pays = insurance.breakdown[i]?.pays ?? 0;
          const room = Math.max(0, annualMax - ytdUsed - pays);
          const name = resolveInsurerName(entry.provider, entry.providerOther)
            ?? ENTRY_LABELS[i]
            ?? `Plan ${i + 1}`;
          return { name, room, annualMax };
        })
        .filter((row): row is { name: string; room: number; annualMax: number } => row !== null)
    : [];

  return (
    <div className="relative overflow-hidden bg-linear-to-br from-[#fde6ec] via-[#f8d7e1] to-[#ecc7d4] p-6 dark:from-[#2a1418] dark:via-[#241015] dark:to-[#1a0c10] sm:p-8">
      {/* Layered radial accents — inline-styled so Tailwind doesn't drop the
          comma-containing arbitrary class in some build paths. */}
      <div
        className="pointer-events-none absolute inset-0 dark:hidden"
        style={{
          backgroundImage:
            "radial-gradient(circle at 100% 0%, rgba(224,85,116,0.32), transparent 55%), radial-gradient(circle at 0% 100%, rgba(167,139,250,0.28), transparent 55%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 hidden dark:block"
        style={{
          backgroundImage:
            "radial-gradient(circle at 100% 0%, rgba(224,85,116,0.22), transparent 55%), radial-gradient(circle at 0% 100%, rgba(167,139,250,0.18), transparent 55%)",
        }}
      />
      {/* decorative blurred orbs for depth */}
      <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#e05574]/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-16 h-52 w-52 rounded-full bg-[#a78bfa]/25 blur-3xl" />
      {/* top highlight line */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/60 to-transparent dark:via-white/15" />

      <div className="relative">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#b83058] dark:text-[#e8798f]">
              Live estimate
            </p>
            <span className="rounded-full border border-[#e8c8ce] bg-white/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#b83058] dark:border-white/15 dark:bg-white/10 dark:text-[#e8798f]">
              Estimate only
            </span>
          </div>
          <span className="flex items-center gap-1.5 text-[11px] text-[#888] dark:text-white/50">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#e05574] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#e05574]" />
            </span>
            updates as you type
          </span>
        </div>
        <p className="mb-5 text-xs leading-relaxed text-[#888] dark:text-white/55">
          Numbers move as you change inputs — none of this charges you.
        </p>

        {/* Donut */}
        <div className="flex justify-center">
          <DonutChart
            segments={segments}
            centerLabel={ready ? "You pay" : "—"}
            centerValue={ready ? insurance.finalPatient : null}
            empty={!ready}
          />
        </div>

        {/* Stats */}
        <div className="mt-6 space-y-2">
          {!ready ? (
            <div className="rounded-2xl border border-dashed border-[#e8c8ce] bg-white/50 p-4 text-center text-sm text-[#666] dark:border-white/10 dark:bg-white/5 dark:text-white/60">
              {incomeRequired
                ? "Enter your drug price and family income to see your estimate."
                : "Enter your drug price to see your estimate."}
            </div>
          ) : (
            <>
              <StatRow
                label="You pay"
                value={insurance.finalPatient}
                color={COLORS.you}
                emphasis
              />
              {hasInsurance && insurance.breakdown.length > 1
                ? insurance.breakdown.map((b, i) => (
                    <StatRow
                      key={`ins-${i}`}
                      label={
                        b.providerName
                          ? `${b.providerName} pays`
                          : `${ENTRY_LABELS[i] ?? `Plan ${i + 1}`} pays`
                      }
                      value={b.pays}
                      color={COLORS.insurance}
                    />
                  ))
                : hasInsurance && (
                    <StatRow
                      label={
                        insurance.breakdown[0]?.providerName
                          ? `${insurance.breakdown[0].providerName} pays`
                          : "Insurance pays"
                      }
                      value={insurance.insurancePays}
                      color={COLORS.insurance}
                    />
                  )}
              <StatRow
                label="PharmaCare pays"
                value={result!.pharmaCarePaysCad}
                color={COLORS.pharmacare}
              />
              {result!.annualDeductibleCad > 0 && (
                <div className="flex items-center justify-between border-t border-[#e8c8ce] pt-2.5 text-xs text-[#666] dark:border-white/10 dark:text-white/60">
                  <span>Annual deductible</span>
                  <AnimatedDollar
                    value={result!.annualDeductibleCad}
                    className="font-medium text-[#444] dark:text-white/80"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Notes */}
        {ready && result && (
          <div className="mt-5 space-y-2 text-xs leading-relaxed text-[#666] dark:text-white/65">
            {result.annualFamilyMaxCad > 0 && (
              <p>
                Once your family reaches {formatPrice(result.annualFamilyMaxCad)} in eligible drug spend, PharmaCare covers 100%.
              </p>
            )}
            {insuranceRoomLines.map((row, i) => (
              <p key={`room-${i}`}>
                Room remaining on {row.name}:{" "}
                <strong>{formatPrice(row.room)}</strong>{" "}
                of {formatPrice(row.annualMax)} annual benefit.
              </p>
            ))}
            <p className="text-[#444] dark:text-white/80">{result.explanation}</p>
          </div>
        )}

        {/* Add to Cart — only when this estimator is on a product page (prefillPrice set) */}
        {prefillPrice && prefillPrice > 0 && (
          <a
            href="#product-add-to-cart"
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#111] px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-[#333] hover:shadow-lg dark:bg-white dark:text-[#111] dark:hover:bg-gray-200"
          >
            <ShoppingCart size={16} />
            Add to Cart — {formatPrice(prefillPrice)}
          </a>
        )}

        {/* Pharmacist disclaimer — prominent, not fine print */}
        <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-[#e8c8ce] bg-white/60 p-3.5 dark:border-white/10 dark:bg-white/5">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-[#b83058] dark:text-[#e8798f]" />
          <p className="text-xs leading-relaxed text-[#555] dark:text-white/70">
            <strong className="text-[#111] dark:text-white">This is an estimate, not a final price.</strong>{" "}
            The pharmacist confirms your actual out-of-pocket cost when processing your order — based on the dispensed product, your pharmacy&apos;s dispensing fee, and your insurer&apos;s real-time adjudication.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Donut chart ── */

function DonutChart({
  segments,
  centerLabel,
  centerValue,
  empty,
}: {
  segments: { label: string; value: number; color: string }[];
  centerLabel: string;
  centerValue: number | null;
  empty: boolean;
}) {
  const size = 200;
  const stroke = 24;
  const radius = (size - stroke) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  // Build accumulator-driven segment data, then animate `dash` per segment via state.
  const targetSegments = useMemo(() => {
    let cumulative = 0;
    return segments.map((seg, i) => {
      const portion = total > 0 ? seg.value / total : 0;
      const startAngle = total > 0 ? (cumulative / total) * 360 - 90 : -90;
      const length = portion * circumference;
      const gap = segments.length > 1 ? 3 : 0;
      const visibleLength = Math.max(0, length - gap);
      cumulative += seg.value;
      return { id: `seg-${i}-${seg.color}`, color: seg.color, startAngle, visibleLength };
    });
  }, [segments, circumference, total]);

  return (
    <div
      className="relative transition-transform duration-300 ease-out"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-[#f0e5e8] dark:stroke-white/5"
        />

        {/* Segments */}
        {!empty &&
          total > 0 &&
          targetSegments.map((seg) => (
            <circle
              key={seg.id}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeLinecap="butt"
              strokeDasharray={`${seg.visibleLength} ${circumference}`}
              transform={`rotate(${seg.startAngle} ${center} ${center})`}
              style={{
                transition:
                  "stroke-dasharray 0.55s cubic-bezier(0.34, 1.3, 0.64, 1), transform 0.55s cubic-bezier(0.34, 1.3, 0.64, 1)",
              }}
            />
          ))}
      </svg>

      {/* Center text */}
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[11px] font-medium uppercase tracking-wider text-[#888] dark:text-white/50">
          {centerLabel}
        </span>
        {empty || centerValue == null ? (
          <span className="mt-0.5 text-2xl font-bold text-[#bbb] dark:text-white/30">—</span>
        ) : (
          <AnimatedDollar
            value={centerValue}
            className="mt-0.5 text-3xl font-bold text-[#e05574]"
          />
        )}
      </div>
    </div>
  );
}

/* ── Stat row ── */

function StatRow({
  label,
  value,
  color,
  emphasis,
}: {
  label: string;
  value: number;
  color: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-white/70 px-3.5 py-2.5 backdrop-blur-sm transition-colors dark:bg-white/5">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span
          className={`truncate text-sm ${
            emphasis
              ? "font-semibold text-[#111] dark:text-white"
              : "text-[#444] dark:text-white/80"
          }`}
        >
          {label}
        </span>
      </div>
      <AnimatedDollar
        value={value}
        className={`font-bold ${emphasis ? "text-lg" : "text-base"}`}
      />
    </div>
  );
}
